import { Cart, CartItem } from "./types";

import { CartState } from "./state";
import { logError } from "../../utils/logger";

export type Action =
  | { type: "CREATE_CART_STARTED" }
  | { type: "CREATE_CART_COMPLETED"; cart: Cart; isCartOpen?: boolean; changeCartOnMerge?: boolean; fetchingCart?: boolean }
  | { type: "FETCH_CART_STARTED" }
  | { type: "FETCH_CART_COMPLETED"; cart: Cart }
  | { type: "MERGE_CART_STARTED" }
  | { type: "MERGE_CART_COMPLETED" }
  | { type: "ADD_ITEM_STARTED"; behindTheScene?: boolean}
  | { type: "ADD_ITEM_COMPLETED"; cart: Cart; isCartOpen?: boolean }
  | { type: "UPDATE_ITEM_STARTED"; behindTheScene?: boolean }
  | { type: "UPDATE_ITEM_COMPLETED"; cart: Cart; isCartOpen?: boolean }
  | { type: "REMOVE_ITEM_STARTED"; behindTheScene?: boolean }
  | { type: "REMOVE_ITEM_COMPLETED"; cart: Cart; isCartOpen?: boolean }
  | { type: "HANDLE_REDIRECT_TO_CHECKOUT_STARTED" }
  | { type: "HANDLE_REDIRECT_TO_CHECKOUT_COMPLETED"; isRedirecting: boolean }
  | { type: "UPDATE_CART_OPEN"; isCartOpen: boolean }
  | { type: "UPDATE_CART_EMAIL"; email: string }
  | { type: "SET_GEO_LOC_REGION"; email: string }
  | { type: "ADD_COUPON_STARTED"; behindTheScene?: boolean }
  | { type: "ADD_COUPON_COMPLETED"; cart: Cart; isCartOpen?: boolean }
  | { type: "REMOVE_COUPON_STARTED"; behindTheScene?: boolean }
  | { type: "REMOVE_COUPON_COMPLETED"; cart: Cart; isCartOpen?: boolean }
  | { type: "UPDATE_SOLD_OUT_PRODUCTS", soldOutProducts: Array<CartItem> }
  | { type: "ERROR"; error: Error }
  | { type: "ADD_ITEM_OPTIMISTIC"; payload: { cartItemId: string; offer: any; tempId: string } }
  | { type: "ADD_ITEM_ROLLBACK"; payload: { cartItemId: string; tempId: string } }
  | { type: "UPDATE_ITEM_OPTIMISTIC"; payload: { cartItemId: string; productId: string | number; quantity: number; originalQuantity: number } }
  | { type: "UPDATE_ITEM_ROLLBACK"; payload: { cartItemId: string; originalQuantity: number } }
  | { type: "REMOVE_ITEM_OPTIMISTIC"; payload: { cartItemId: string; savedItem: any } }
  | { type: "REMOVE_ITEM_ROLLBACK"; payload: { savedItem: any } };

export const reducer = (state: CartState, action: Action): CartState => {
  switch (action.type) {
    case "ADD_ITEM_STARTED":
    case "UPDATE_ITEM_STARTED":
    case "REMOVE_ITEM_STARTED":
    case "ADD_COUPON_STARTED":
    case "REMOVE_COUPON_STARTED":
      return {
        ...state,
        isLoading: action?.behindTheScene ? false : true,
        isSilentProcessing: action?.behindTheScene,
      };

    case "CREATE_CART_STARTED":
    case "FETCH_CART_STARTED":
      return {
        ...state,
        isLoading: true,
        fetchingCart: true,
      };

    case "CREATE_CART_COMPLETED":
      return {
        ...state,
        cart: action.cart,
        isCartOpen:
          action.isCartOpen !== undefined
            ? action.isCartOpen
            : state.isCartOpen,
        isLoading: false,
        changeCartOnMerge: action.changeCartOnMerge,
        fetchingCart: action.fetchingCart,
      };

    case "FETCH_CART_COMPLETED":
      return {
        ...state,
        cart: action.cart,
        isCartOpen: false,
        isLoading: false,
        fetchingCart: false,
      };

    case "ADD_ITEM_COMPLETED":
      return {
        ...state,
        cart: action.cart,
        isCartOpen: false,
        isLoading: false,
        isSilentProcessing: false,
      };

    case "UPDATE_ITEM_COMPLETED":
      return {
        ...state,
        cart: action.cart,
        isCartOpen:
          action.isCartOpen !== undefined
            ? action.isCartOpen
            : state.isCartOpen,
        isLoading: false,
        isSilentProcessing: false,
      };

    case "REMOVE_ITEM_COMPLETED":
      return {
        ...state,
        cart: action.cart,
        isLoading: false,
        isSilentProcessing: false,
      };

    case "HANDLE_REDIRECT_TO_CHECKOUT_STARTED":
      return {
        ...state,
        isRedirecting: true,
      };

    case "HANDLE_REDIRECT_TO_CHECKOUT_COMPLETED":
      return {
        ...state,
        isRedirecting: action.isRedirecting,
        isCartOpen: false,
      };

    case "UPDATE_CART_OPEN":
      return {
        ...state,
        isCartOpen: action.isCartOpen,
      };

    case "UPDATE_CART_EMAIL":
      return {
        ...state,
        cart: { ...state.cart, customerEmail: action.email },
      };

    case "SET_GEO_LOC_REGION":
      return {
        ...state,
        geoLocRegion: action.region,
      };

    case "MERGE_CART_STARTED":
      return {
        ...state,
        isMerging: true,
      };

    case "MERGE_CART_COMPLETED":
      return {
        ...state,
        isMerging: false,
      };

    case "ADD_COUPON_COMPLETED":
    case "REMOVE_COUPON_COMPLETED":
      return {
        ...state,
        cart: action.cart,
        isLoading: false,
        isSilentProcessing: false,
      };

    case "UPDATE_SOLD_OUT_PRODUCTS":
      return {
        ...state,
        soldOutProducts: action.soldOutProducts,
      };

    case "ERROR":
      logError(action?.error?.message, { error: action?.error });
      return {
        ...state,
        isLoading: false,
        isRedirecting: false,
        error: action?.error,
        isSilentProcessing: false,
      };

    case "ADD_ITEM_OPTIMISTIC": {
      const { cartItemId, offer, tempId } = action.payload;
      const currentCart = state.cart || {
        id: "",
        lineItems: [],
        cartAmount: 0,
        cartQuantity: 0,
        shippingTotal: 0,
        cartTotal: 0,
      } as any;
      
      const pId = parseInt(offer?.productId?.toString() || offer?.bigcommerceId || cartItemId);
      const existingItemIndex = currentCart.lineItems.findIndex((i: any) => i.productId === pId);

      let newLineItems = [...currentCart.lineItems];
      if (existingItemIndex >= 0) {
        newLineItems[existingItemIndex] = {
          ...newLineItems[existingItemIndex],
          quantity: newLineItems[existingItemIndex].quantity + 1,
        };
      } else {
        newLineItems.push({
          id: tempId,
          productId: pId,
          slug: offer?.slug || "",
          sku: offer?.sku?.code || "",
          name: offer?.name || "",
          imageUrl: offer?.images?.[0]?.url || "",
          quantity: 1,
          listPrice: offer?.prices?.listPrice || 0,
          salePrice: offer?.prices?.salePrice || 0,
          comparisonPrice: offer?.prices?.comparisonPrice || 0,
          extendedListPrice: offer?.prices?.listPrice || 0,
          extendedSalePrice: offer?.prices?.salePrice || 0,
          extendedComparisonPrice: offer?.prices?.comparisonPrice || 0,
          discountAmount: 0,
          addedByPromotion: false
        });
      }

      return {
        ...state,
        cart: {
          ...currentCart,
          lineItems: newLineItems,
          cartQuantity: currentCart.cartQuantity + 1,
        },
        isLoading: false,
        isSilentProcessing: true,
      };
    }

    case "ADD_ITEM_ROLLBACK": {
      const { tempId, cartItemId } = action.payload;
      if (!state.cart) return state;

      let newLineItems = state.cart.lineItems.map((item: any) => {
        if (item.id === tempId || item.productId?.toString() === cartItemId) {
          return { ...item, quantity: Math.max(0, item.quantity - 1) };
        }
        return item;
      }).filter((item: any) => item.quantity > 0);

      return {
        ...state,
        cart: {
          ...state.cart,
          lineItems: newLineItems,
          cartQuantity: Math.max(0, state.cart.cartQuantity - 1),
        },
        isLoading: false,
        isSilentProcessing: false,
      };
    }

    case "UPDATE_ITEM_OPTIMISTIC": {
      const { cartItemId, quantity, originalQuantity } = action.payload;
      if (!state.cart) return state;

      const delta = quantity - originalQuantity;

      let newLineItems = state.cart.lineItems.map((item: any) => {
        if (item.id === cartItemId) {
          return { ...item, quantity };
        }
        return item;
      });

      return {
        ...state,
        cart: {
          ...state.cart,
          lineItems: newLineItems,
          cartQuantity: Math.max(0, state.cart.cartQuantity + delta),
        },
        isLoading: false,
        isSilentProcessing: true,
      };
    }

    case "UPDATE_ITEM_ROLLBACK": {
      const { cartItemId, originalQuantity } = action.payload;
      if (!state.cart) return state;

      const currentItem = state.cart.lineItems.find((item: any) => item.id === cartItemId);
      const currentQuantity = currentItem?.quantity || originalQuantity;
      const delta = originalQuantity - currentQuantity;

      let newLineItems = state.cart.lineItems.map((item: any) => {
        if (item.id === cartItemId) {
          return { ...item, quantity: originalQuantity };
        }
        return item;
      });

      return {
        ...state,
        cart: {
          ...state.cart,
          lineItems: newLineItems,
          cartQuantity: Math.max(0, state.cart.cartQuantity + delta),
        },
        isLoading: false,
        isSilentProcessing: false,
      };
    }

    case "REMOVE_ITEM_OPTIMISTIC": {
      const { cartItemId } = action.payload;
      if (!state.cart) return state;

      let newLineItems = state.cart.lineItems.filter((item: any) => item.id !== cartItemId);
      
      return {
        ...state,
        cart: {
          ...state.cart,
          lineItems: newLineItems,
          cartQuantity: Math.max(0, state.cart.cartQuantity - action.payload.savedItem.quantity),
        },
        isLoading: false,
        isSilentProcessing: true,
      };
    }

    case "REMOVE_ITEM_ROLLBACK": {
      const { savedItem } = action.payload;
      if (!state.cart || !savedItem) return state;

      let newLineItems = [...state.cart.lineItems, savedItem];

      return {
        ...state,
        cart: {
          ...state.cart,
          lineItems: newLineItems,
          cartQuantity: state.cart.cartQuantity + savedItem.quantity,
        },
        isLoading: false,
        isSilentProcessing: false,
      };
    }

    default:
      return state;
  }
};
